import React from 'react';
import { MessageCircle } from 'lucide-react';
import SeoHead from './SeoHead';
import FAQSection from './FAQSection';
import { pagePaths } from '../utils/sitePaths';

const services = {
  'catering-services-gauteng': {
    title: 'Catering Services Gauteng',
    intro:
      'Ubuntu Events Management delivers catering services in Gauteng for weddings, birthdays, private functions, and corporate events across Pretoria, Johannesburg, Centurion, Midrand, Sandton, Boksburg, Kempton Park, Alberton, and Soweto.',
    breakdown: ['Buffet menus', 'Spit braai catering', 'Corporate catering', 'Celebration platters'],
    includes: ['Menu planning and portioning', 'Delivery, setup, and serving support', 'Dietary accommodation options', 'WhatsApp-first booking and updates'],
    benefits: 'If you are searching for catering services near me in Gauteng, our reliable service and experienced team help you move from inquiry to confirmed booking fast.',
  },
  'spit-braai-hire-gauteng': {
    title: 'Spit Braai Hire Gauteng',
    intro:
      'Book spit braai hire in Gauteng with full setup support and event-day coordination in Pretoria, Johannesburg, Centurion, Midrand, Sandton, Boksburg, Kempton Park, Alberton, and Soweto.',
    breakdown: ['Spit braai machine hire', 'Braai master support', 'Premium meat menu options', 'On-site event setup'],
    includes: ['Machine and operational guidance', 'Optional staffing packages', 'Menu add-ons and sides', 'Fast WhatsApp quote turnaround'],
    benefits: 'Great for private and corporate events in Gauteng where flavor, atmosphere, and dependable coordination matter.',
  },
  'event-decor-gauteng': {
    title: 'Event Decor Gauteng',
    intro:
      'Ubuntu Events Management provides event decor in Gauteng for elegant weddings, birthdays, and corporate functions in Pretoria, Johannesburg, Centurion, Midrand, Sandton, Boksburg, Kempton Park, Alberton, and Soweto.',
    breakdown: ['Wedding decor and styling', 'Birthday decor themes', 'Corporate event styling', 'Floral and centerpiece setups'],
    includes: ['Setup and styling team', 'Theme planning consultation', 'Decor item logistics', 'Optional add-on coordination'],
    benefits: 'Ideal for clients needing polished visual impact without managing multiple decor suppliers.',
  },
  'sound-hire-gauteng': {
    title: 'Sound Hire Gauteng',
    intro:
      'Need sound hire near me in Gauteng? We supply clear, reliable audio for events in Pretoria, Johannesburg, Centurion, Midrand, Sandton, Boksburg, Kempton Park, Alberton, and Soweto.',
    breakdown: ['PA and speaker systems', 'Wireless microphones', 'Speech and DJ setups', 'Smoke and effect add-ons'],
    includes: ['Package matching to venue size', 'Setup and sound checks', 'Optional on-site support', 'Flexible add-on selection'],
    benefits: 'Ensure speeches, music, and program flow remain clear and professional throughout your event.',
  },
  'tent-chair-table-hire-gauteng': {
    title: 'Tent Chair Table Hire Gauteng',
    intro:
      'Get tent, chair, and table hire in Gauteng for private and corporate events across Pretoria, Johannesburg, Centurion, Midrand, Sandton, Boksburg, Kempton Park, Alberton, and Soweto.',
    breakdown: ['Tent hire options', 'Chair hire quantities', 'Table hire packages', 'Delivery and setup support'],
    includes: ['Quantity planning based on guest count', 'Setup and collection logistics', 'Event-day timeline coordination', 'Quote updates via WhatsApp'],
    benefits: 'A practical near me option for event hire in Gauteng with one reliable service partner.',
  },
  'team-building-gauteng': {
    title: 'Team Building Gauteng',
    intro:
      'Our team building Gauteng packages help organizations in Pretoria, Johannesburg, Centurion, Midrand, Sandton, Boksburg, Kempton Park, Alberton, and Soweto improve team energy and connection.',
    breakdown: ['Outdoor challenge activities', 'Collaboration games', 'Corporate team experiences', 'Catering and venue bundles'],
    includes: ['Activity planning and facilitation', 'Team size customization', 'Optional catering and sound support', 'Post-event coordination guidance'],
    benefits: 'Designed for businesses that want measurable engagement with smooth logistics and minimal admin effort.',
  },
  'venue-booking-gauteng': {
    title: 'Venue Booking Gauteng',
    intro:
      'Ubuntu Events Management offers venue booking support in Gauteng, helping you shortlist, compare, and secure venues in Pretoria, Johannesburg, Centurion, Midrand, Sandton, Boksburg, Kempton Park, Alberton, and Soweto.',
    breakdown: ['Venue sourcing support', 'Venue comparison and fit checks', 'Booking coordination', 'Supplier alignment'],
    includes: ['Location and budget matching', 'Indoor/outdoor suitability checks', 'Supplier scheduling alignment', 'WhatsApp booking updates'],
    benefits: 'Reduce venue selection stress while staying aligned with your event services Gauteng timeline and budget.',
  },
};

const commonFaqSet = [
  { q: 'Do you offer this service near me in Gauteng?', a: 'Yes. We serve Pretoria, Johannesburg, Centurion, Midrand, Sandton, Boksburg, Kempton Park, Alberton, and Soweto.' },
  { q: 'How quickly can I get a quote in Gauteng?', a: 'Most clients receive a same-day WhatsApp response with an initial estimate and clear next steps.' },
  { q: 'Can I combine multiple services in one booking?', a: 'Yes. Ubuntu Events Management is a full-service event solutions company, so we can combine catering, decor, sound hire, equipment hire, team building, and venue support.' },
  { q: 'Do you do weekend events in Gauteng?', a: 'Yes. Weekend bookings fill fast and dates are limited, so early confirmation is recommended.' },
  { q: 'Do you support private and corporate functions?', a: 'Yes, we support private celebrations, corporate events, activations, conferences, and team days.' },
  { q: 'How do I book quickly?', a: 'Use Estimate & Book or click WhatsApp to submit details in 1-2 clicks and receive booking guidance.' },
  { q: 'Do you work across Pretoria and Johannesburg corridors?', a: 'Yes, our teams operate across Pretoria, Johannesburg, Centurion, Midrand, and nearby Gauteng areas.' },
];

const navLinks = [
  { label: 'Home', page: 'home' },
  { label: 'Catering', page: 'menu' },
  { label: 'Spit Braai', page: 'spitbraai' },
  { label: 'Decor', page: 'decor' },
  { label: 'Sound Hire', page: 'sound' },
  { label: 'Venue Support', page: 'venuesupport' },
  { label: 'Team Building', page: 'teambuilding' },
  { label: 'Estimate & Book', page: 'estimate' },
  { label: 'Blog', page: 'blog' },
];

const ServiceLandingPage = ({ slug }) => {
  const data = services[slug] || services['catering-services-gauteng'];
  const navigate = (page, event) => {
    if (event) event.preventDefault();
    window.dispatchEvent(new CustomEvent('navigate', { detail: page }));
  };
  const whatsappMessage = `Hi Ubuntu Events Management, I need help with ${data.title} in Gauteng. Please share packages, availability, and pricing for my area.`;

  return (
    <div className="min-h-screen py-10 px-4">
      <SeoHead
        title={`${data.title} | Ubuntu Enterprises Gauteng Event Services`}
        description={`${data.title} for reliable Gauteng event services in Pretoria, Centurion, Midrand, and Johannesburg.`}
        canonicalPath={`/${slug}`}
        faqSchema={commonFaqSet}
      />
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-4">
          {data.title}
        </h1>
        <p className="text-lg text-gray-700 mb-8">{data.intro}</p>
        <p className="text-sm text-gold font-semibold mb-8">Limited availability. Weekend bookings fill fast.</p>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="card">
            <h2 className="text-2xl font-playfair font-bold mb-3">Service Breakdown</h2>
            <ul className="space-y-2 text-gray-700">
              {data.breakdown.map((item) => (
                <li key={item}>- {item}</li>
              ))}
            </ul>
          </div>
          <div className="card">
            <h2 className="text-2xl font-playfair font-bold mb-3">What Is Included</h2>
            <ul className="space-y-2 text-gray-700">
              {data.includes.map((item) => (
                <li key={item}>- {item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="card mb-8">
          <h2 className="text-2xl font-playfair font-bold mb-3">Benefits</h2>
          <p className="text-gray-700 mb-5">{data.benefits}</p>
          <a
            href={`https://wa.me/27663286231?text=${encodeURIComponent(whatsappMessage)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
          >
            <MessageCircle size={18} />
            WhatsApp for Fast Quote
          </a>
        </div>

        <div className="card mb-8 bg-gradient-to-r from-gold/10 to-olive/10 border border-gold/20">
          <h2 className="text-2xl font-playfair font-bold mb-3">Why Choose Ubuntu Enterprises</h2>
          <p className="text-gray-700 mb-4">
            Ubuntu Events Management is a full-service event solutions company offering catering, decor, sound hire, equipment hire, team building, and venue support across Gauteng.
            Reliable service, experienced team, and complete event services Gauteng support from first message to event day.
          </p>
          <a
            href={`https://wa.me/27663286231?text=${encodeURIComponent(whatsappMessage)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
          >
            <MessageCircle size={18} />
            Chat on WhatsApp
          </a>
        </div>

        <div className="card">
          <h2 className="text-2xl font-playfair font-bold mb-4">Internal Links and Next Steps</h2>
          <div className="flex flex-wrap gap-3">
            {navLinks.map((link) => (
              <a
                key={link.page}
                href={pagePaths[link.page]}
                onClick={(e) => navigate(link.page, e)}
                className="btn-secondary text-sm py-2 px-4"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>
      <FAQSection title={`FAQs: ${data.title}`} faqs={commonFaqSet} />
    </div>
  );
};

export default ServiceLandingPage;
